/* Unified Inbox - middle pane: thread header + actions, AI brief, channel-aware message timeline, composer with AI drafting. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, Clock, Paperclip, Send, Sparkles, StickyNote, UserPlus, MoreHorizontal, Pin, PinOff, MailOpen, Mic, FileText, PanelRight, ExternalLink, Target, RotateCcw, Loader2, Languages, Smile, Meh, Frown, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtDateTime, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CompanyLogo, UserAvatar, LightMarkdown, Skeleton, EmptyState, InlineError } from "@/components/common";
import { EvidenceLabel } from "@/components/intel/primitives";
import { ChannelBadge, PriorityChip } from "./ThreadList";
import { INTENT, LANG, SENTIMENT, EVIDENCE_KIND, channelMeta, dueLabel } from "./meta";

const SENT_ICON = { positive: Smile, neutral: Meh, frustrated: Frown };

/* ---------------------------------------------------------------- AI brief */
function AiBrief({ ai, onUseDraft, onAct }) {
  const [open, setOpen] = useState(false);
  if (!ai) return null;
  const intent = INTENT[ai.intent] || { label: ai.intent };
  const IntentIcon = intent.Icon || Sparkles;
  const SIcon = SENT_ICON[ai.sentiment] || Meh;
  const due = dueLabel(ai.due_at);
  const ents = Object.entries(ai.entities || {}).filter(([, v]) => v && (Array.isArray(v) ? v.length : true));
  return (
    <section className="relative overflow-hidden rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-white shadow-[0_1px_2px_hsl(30_12%_18%/0.04)]" data-testid="inbox-ai-brief">
      <span className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full bg-[hsl(var(--primary)/0.10)] blur-3xl" />
      <div className="relative px-5 pb-4 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow inline-flex items-center gap-1.5 text-primary"><Sparkles className="h-3 w-3" /> AI brief</span>
          <span className="inline-flex items-center gap-1 rounded-md border border-black/[0.08] bg-white px-1.5 py-[2px] text-[10.5px] font-medium text-foreground/80" data-testid="inbox-ai-intent"><IntentIcon className="h-3 w-3" /> {intent.label}</span>
          <PriorityChip priority={ai.priority} />
          <span className={cn("inline-flex items-center gap-1 text-[11px] font-medium", (SENTIMENT[ai.sentiment] || SENTIMENT.neutral).cls)} data-testid="inbox-ai-sentiment"><SIcon className="h-3.5 w-3.5" /> {(SENTIMENT[ai.sentiment] || SENTIMENT.neutral).label}</span>
          {due ? <span className={cn("ml-auto inline-flex items-center gap-1 text-[11.5px] font-medium", due.tone === "bad" ? "text-[hsl(var(--status-bad))]" : due.tone === "warn" ? "text-[hsl(35_80%_32%)]" : "text-muted-foreground")} data-testid="inbox-ai-due"><Clock className="h-3.5 w-3.5" /> {due.text}</span> : null}
        </div>
        <p className="mt-2.5 text-[14.5px] leading-relaxed text-foreground" data-testid="inbox-ai-summary">{ai.summary}</p>
        {ai.why ? <p className="mt-1 text-[12.5px] text-muted-foreground"><span className="font-medium text-foreground/70">Why it matters · </span>{ai.why}</p> : null}

        <div className="mt-3 flex flex-col gap-2 rounded-xl bg-[hsl(var(--muted)/0.6)] px-3.5 py-3 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <div className="eyebrow text-[10px]">Next best action</div>
            <div className="mt-0.5 text-[13.5px] font-medium leading-snug" data-testid="inbox-ai-next-action">{ai.next_action}</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {ai.draft ? <Button size="sm" variant="outline" className="h-8 rounded-full bg-white" onClick={onUseDraft} data-testid="inbox-ai-use-draft"><FileText className="mr-1.5 h-3.5 w-3.5" /> Use suggested reply</Button> : null}
            <Button size="sm" className="h-8 rounded-full" onClick={onAct} data-testid="inbox-ai-act"><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Draft with AI</Button>
          </div>
        </div>

        {ents.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="inbox-ai-entities">
            {ents.map(([k, v]) => (
              <span key={k} className="inline-flex max-w-full items-center gap-1 rounded-md border border-black/[0.07] bg-white px-2 py-1 text-[11.5px]">
                <span className="font-semibold uppercase tracking-[0.08em] text-muted-foreground text-[9.5px]">{k.replace(/_/g, " ")}</span>
                <span className="truncate text-foreground/90">{Array.isArray(v) ? v.join(", ") : v}</span>
              </span>
            ))}
          </div>
        ) : null}

        {(ai.requirements?.length || ai.evidence?.length) ? (
          <button type="button" onClick={() => setOpen((v) => !v)} className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground" data-testid="inbox-ai-toggle-details">
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} /> {open ? "Hide" : "Show"} requirements & evidence
          </button>
        ) : null}
        {open ? (
          <div className="mt-3 grid gap-4 md:grid-cols-2" data-testid="inbox-ai-details">
            {ai.requirements?.length ? (
              <div>
                <div className="eyebrow mb-1.5 text-[10px]">Extracted requirements</div>
                <ul className="space-y-1.5">
                  {ai.requirements.map((r) => (
                    <li key={r} className="flex gap-2 text-[13px] leading-snug"><Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-ok))]" /> <span>{r}</span></li>
                  ))}
                </ul>
              </div>
            ) : null}
            {ai.evidence?.length ? (
              <div>
                <div className="eyebrow mb-1.5 text-[10px]">Evidence</div>
                <ul className="space-y-2">
                  {ai.evidence.map((e, i) => (
                    <li key={i} className="text-[12.5px] leading-snug">
                      <EvidenceLabel kind={EVIDENCE_KIND[e.label] || "fact"} className="mb-1" />
                      <div className="text-foreground/85">{e.text}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- messages */
function Attachment({ a }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-black/[0.08] bg-white px-2 py-1 text-[12px]" data-testid="inbox-attachment">
      <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate font-medium">{a.name}</span>
      <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground">{a.size}</span>
    </span>
  );
}

function Message({ m, thread }) {
  const out = m.direction === "out";
  const note = m.direction === "internal";
  const ch = channelMeta(m.channel);
  const author = m.author || {};
  const isInternalAuthor = author.kind === "internal";
  const voicemail = m.kind === "voicemail";
  const form = m.kind === "form";
  const notice = m.kind === "tender_notice";
  return (
    <article className={cn("msg-enter flex gap-3", out && "flex-row-reverse")} data-testid={`inbox-message-${m.id}`} data-direction={m.direction}>
      <div className="mt-1 shrink-0">
        {isInternalAuthor ? <UserAvatar name={author.name} src={author.user_id ? `/avatars/${author.user_id.replace("u_", "")}.jpg` : null} size="md" /> : <CompanyLogo name={thread.company?.name} domain={thread.company?.website} size="md" />}
      </div>
      <div className={cn("min-w-0 max-w-[720px] flex-1", out && "flex flex-col items-end")}>
        <div className={cn("mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px]", out && "flex-row-reverse")}>
          <span className="font-semibold text-foreground">{author.name}</span>
          {author.role ? <span className="text-muted-foreground">{author.role}</span> : null}
          <span className="inline-flex items-center gap-1 text-muted-foreground"><ch.Icon className="h-3 w-3" /> {note ? "Internal note" : voicemail ? "Voicemail" : form ? "Web form" : notice ? "Portal notice" : ch.short}</span>
          <span className="font-mono text-[10.5px] text-muted-foreground" title={fmtDateTime(m.sent_at)}>{relTime(m.sent_at)}</span>
        </div>
        <div
          className={cn(
            "rounded-2xl px-4 py-3 text-[14px] leading-relaxed",
            note && "border border-dashed border-[hsl(var(--status-warn)/0.45)] bg-[hsl(var(--status-warn)/0.07)] text-foreground",
            out && !note && "bg-[hsl(var(--ink))] text-white",
            !out && !note && "border border-black/[0.07] bg-white shadow-[0_1px_2px_hsl(30_12%_18%/0.04)]",
            notice && "font-mono text-[12.5px]"
          )}
        >
          {voicemail ? (
            <div className="mb-2 flex items-center gap-2 text-[11.5px] font-medium text-muted-foreground">
              <Mic className="h-3.5 w-3.5" /> AI transcript · verify details on the call
              <span className="ml-1 flex h-3 items-end gap-[2px]" aria-hidden="true">{[3, 6, 9, 5, 11, 7, 4, 8, 6, 10, 5, 3].map((h, i) => <span key={i} className="w-[2px] rounded-full bg-muted-foreground/50" style={{ height: h }} />)}</span>
            </div>
          ) : null}
          {form ? <LightMarkdown text={m.body} className="[&_strong]:text-[13px] [&_strong]:uppercase [&_strong]:tracking-[0.1em] [&_strong]:text-muted-foreground" /> : <div className="whitespace-pre-wrap">{m.body}</div>}
          {m.attachments?.length ? <div className="mt-3 flex flex-wrap gap-1.5">{m.attachments.map((a) => <Attachment key={a.name} a={a} />)}</div> : null}
        </div>
      </div>
    </article>
  );
}

/* ---------------------------------------------------------------- composer */
function Composer({ thread, value, onChange, onSend, onDraft, drafting, draftNotes, kind, onKind, sending, textareaRef }) {
  const ch = channelMeta(thread.channel);
  const chat = ["teams", "whatsapp", "sms", "linkedin"].includes(thread.channel);
  return (
    <div className="shrink-0 border-t border-black/[0.06] bg-[hsl(var(--background))] px-4 py-3 sm:px-6" data-testid="inbox-composer">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-black/[0.06] bg-[hsl(40_10%_95%)] p-0.5">
          <button type="button" onClick={() => onKind("reply")} data-testid="inbox-composer-kind-reply" className={cn("inline-flex h-7 items-center gap-1.5 rounded-[7px] px-2.5 text-[12.5px] font-medium", kind === "reply" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}><ch.Icon className="h-3.5 w-3.5" /> Reply via {ch.short}</button>
          <button type="button" onClick={() => onKind("note")} data-testid="inbox-composer-kind-note" className={cn("inline-flex h-7 items-center gap-1.5 rounded-[7px] px-2.5 text-[12.5px] font-medium", kind === "note" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}><StickyNote className="h-3.5 w-3.5" /> Internal note</button>
        </div>
        <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground"><Languages className="h-3.5 w-3.5" /> {LANG[thread.language] || "English"}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 rounded-full bg-white" disabled={drafting} data-testid="inbox-draft-ai-button">
                {drafting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5 text-primary" />} Draft with AI <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Tone · {LANG[thread.language] || "English"}</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onDraft("concise")} data-testid="inbox-draft-tone-concise">Concise and concrete</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDraft("warm")} data-testid="inbox-draft-tone-warm">Warm and personal</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDraft("formal")} data-testid="inbox-draft-tone-formal">Formal</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDraft("concise", "Propose two concrete meeting slots next week.")} data-testid="inbox-draft-meeting">Propose a meeting</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDraft("concise", "Ask the clarifying questions we need before we can answer, as a short numbered list.")} data-testid="inbox-draft-questions">Ask clarifying questions</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className={cn("rounded-2xl border bg-white shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] focus-within:border-primary/50 focus-within:ring-[3px] focus-within:ring-primary/15", kind === "note" ? "border-dashed border-[hsl(var(--status-warn)/0.5)]" : "border-black/[0.09]")}>
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={kind === "note" ? "Write an internal note for the team…" : chat ? `Message ${thread.contact?.name?.split(" ")[0] || ""} on ${ch.short}…` : `Reply to ${thread.contact?.name || "the customer"}…`}
          className="min-h-[88px] max-h-[320px] resize-y border-0 bg-transparent px-4 py-3 text-[14px] leading-relaxed shadow-none focus-visible:ring-0"
          data-testid="inbox-composer-textarea"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") onSend();
          }}
        />
        {draftNotes?.length ? (
          <div className="mx-3 mb-2 rounded-xl border border-[hsl(var(--status-warn)/0.35)] bg-[hsl(var(--status-warn)/0.07)] px-3 py-2 text-[12px]" data-testid="inbox-draft-notes">
            <div className="mb-1 inline-flex items-center gap-1 font-semibold text-[hsl(35_80%_32%)]"><AlertCircle className="h-3.5 w-3.5" /> Verify before sending</div>
            <ul className="list-disc space-y-0.5 pl-4 text-foreground/80">{draftNotes.map((n) => <li key={n}>{n}</li>)}</ul>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-2 border-t border-black/[0.05] px-3 py-2">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Tooltip><TooltipTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" disabled aria-label="Attach file"><Paperclip className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>Attachments – coming soon</TooltipContent></Tooltip>
            {value ? <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] hover:bg-black/[0.04] hover:text-foreground" data-testid="inbox-composer-clear"><RotateCcw className="h-3 w-3" /> Clear</button> : null}
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-[11px] text-muted-foreground sm:inline"><span className="kbd">⌘</span> <span className="kbd">↵</span></span>
            <Button size="sm" className={cn("h-8 rounded-full px-4", kind === "note" && "bg-[hsl(var(--ink))] hover:bg-black")} disabled={!value.trim() || sending} onClick={onSend} data-testid="inbox-send-button">
              {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : kind === "note" ? <StickyNote className="mr-1.5 h-3.5 w-3.5" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              {kind === "note" ? "Add note" : "Send reply"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- conversation */
export function Conversation({ data, loading, error, onRetry, users, onPatch, onSend, onDraft, onCreateOpportunity, onStartWorkspace, onBack, onToggleContext, contextOpen, busy }) {
  const [kind, setKind] = useState("reply");
  const [value, setValue] = useState("");
  const [draftNotes, setDraftNotes] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);
  const thread = data?.thread;
  const messages = useMemo(() => data?.messages || [], [data?.messages]);
  const ctx = data?.context;

  useEffect(() => {
    setValue("");
    setDraftNotes([]);
    setKind("reply");
  }, [thread?.id]);

  // a new thread opens at the top (the AI brief first); a message you just sent scrolls into view
  const seen = useRef({ id: null, n: 0 });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (seen.current.id !== thread?.id) {
      el.scrollTop = 0;
    } else if (messages.length > seen.current.n) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
    seen.current = { id: thread?.id, n: messages.length };
  }, [thread?.id, messages.length]);

  const grouped = messages;

  const useDraft = () => {
    if (!thread?.ai?.draft) return;
    setKind("reply");
    setValue(thread.ai.draft);
    setDraftNotes(["Suggested reply was pre-written for this demo thread – adjust names, dates and commitments before sending."]);
    textareaRef.current?.focus();
  };

  const draft = async (tone, instruction) => {
    setDrafting(true);
    try {
      const d = await onDraft(tone, instruction);
      setKind("reply");
      setValue(d.body || "");
      setDraftNotes(d.notes || []);
      textareaRef.current?.focus();
      if (d.source === "template") toast.warning("AI unavailable – inserted a template draft");
    } catch (e) {
      toast.error("Could not draft a reply");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!value.trim()) return;
    setSending(true);
    try {
      await onSend(value, kind);
      setValue("");
      setDraftNotes([]);
      toast.success(kind === "note" ? "Note added" : `Reply sent via ${channelMeta(thread.channel).short}`);
    } catch (e) {
      toast.error("Could not send");
    } finally {
      setSending(false);
    }
  };

  if (error) return <div className="p-6"><InlineError onRetry={onRetry} testId="inbox-thread-error">{error}</InlineError></div>;
  if (loading || !thread) {
    return (
      <div className="flex h-full flex-col" data-testid="inbox-thread-loading">
        <div className="border-b border-black/[0.06] px-6 py-4"><Skeleton className="h-5 w-2/3" /><Skeleton className="mt-2 h-3 w-1/3" /></div>
        <div className="space-y-4 p-6"><Skeleton className="h-40 w-full rounded-2xl" /><Skeleton className="h-24 w-3/4 rounded-2xl" /><Skeleton className="h-20 w-2/3 rounded-2xl" /></div>
      </div>
    );
  }

  const ch = channelMeta(thread.channel);
  const assignee = thread.assignee;
  const opp = ctx?.opportunity;

  return (
    <section className="flex h-full min-h-0 flex-col" data-testid="inbox-conversation" aria-label="Conversation">
      {/* header */}
      <header className="shrink-0 border-b border-black/[0.06] px-4 pb-3 pt-3 sm:px-6">
        <div className="flex items-start gap-3">
          {onBack ? <Button size="icon" variant="ghost" className="-ml-2 mt-0.5 h-8 w-8 lg:hidden" onClick={onBack} aria-label="Back to inbox" data-testid="inbox-back-button"><ArrowLeft className="h-4 w-4" /></Button> : null}
          <div className="relative mt-0.5 shrink-0">
            <CompanyLogo name={thread.company?.name} domain={thread.company?.website} size="lg" />
            <ChannelBadge channel={thread.channel} className="absolute -bottom-1 -right-1" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[17px] font-semibold leading-tight tracking-[-0.01em]" data-testid="inbox-thread-subject" title={thread.subject}>{thread.subject}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-muted-foreground">
              <span className="font-medium text-foreground/85">{thread.company?.name}</span>
              {thread.contact?.name && thread.contact?.kind !== "system" ? <span>· {thread.contact.name}{thread.contact.role ? <span className="hidden sm:inline">, {thread.contact.role}</span> : null}</span> : null}
              <span className="hidden sm:inline">· via {ch.label}</span>
              <span className="hidden sm:inline">· {thread.message_count} messages</span>
              {(thread.labels || []).map((l) => <span key={l} className="rounded-md border border-black/[0.07] bg-white px-1.5 py-[1px] text-[10.5px]">{l}</span>)}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-full bg-white px-2.5" data-testid="inbox-assign-button">
                  {assignee ? <UserAvatar name={assignee.name} initials={assignee.initials} color={assignee.color} src={assignee.avatar_url} size="xs" /> : <UserPlus className="h-3.5 w-3.5" />}
                  <span className="hidden md:inline">{assignee ? assignee.name.split(" ")[0] : "Assign"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Assign to</DropdownMenuLabel>
                {(users || []).map((u) => (
                  <DropdownMenuItem key={u.id} onClick={() => onPatch({ assignee_id: u.id })} data-testid={`inbox-assign-${u.id}`} className="gap-2">
                    <UserAvatar name={u.name} initials={u.initials} color={u.color} src={u.avatar_url} size="xs" /> <span className="flex-1">{u.name}</span> {assignee?.id === u.id ? <Check className="h-3.5 w-3.5" /> : null}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onPatch({ clear_assignee: true })} data-testid="inbox-assign-clear">Unassign</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="hidden h-8 gap-1.5 rounded-full bg-white px-2.5 sm:inline-flex" data-testid="inbox-snooze-button"><Clock className="h-3.5 w-3.5" /> <span className="hidden md:inline">Snooze</span></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onPatch({ status: "snoozed", snooze_days: 1 })} data-testid="inbox-snooze-1">Tomorrow</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPatch({ status: "snoozed", snooze_days: 3 })} data-testid="inbox-snooze-3">In 3 days</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPatch({ status: "snoozed", snooze_days: 7 })} data-testid="inbox-snooze-7">Next week</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {thread.status === "open" ? (
              <Button size="sm" className="h-8 gap-1.5 rounded-full bg-[hsl(var(--ink))] px-3 hover:bg-black" onClick={() => onPatch({ status: "done" })} data-testid="inbox-done-button"><Check className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Done</span></Button>
            ) : (
              <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-full bg-white px-3" onClick={() => onPatch({ status: "open" })} data-testid="inbox-reopen-button"><MailOpen className="h-3.5 w-3.5" /> Reopen</Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="More" data-testid="inbox-more-button"><MoreHorizontal className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onPatch({ pinned: !thread.pinned })} data-testid="inbox-pin-toggle">{thread.pinned ? <PinOff className="mr-2 h-3.5 w-3.5" /> : <Pin className="mr-2 h-3.5 w-3.5" />} {thread.pinned ? "Unpin" : "Pin to top"}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPatch({ unread: true })} data-testid="inbox-mark-unread"><MailOpen className="mr-2 h-3.5 w-3.5" /> Mark as unread</DropdownMenuItem>
                {onToggleContext ? <DropdownMenuItem onClick={onToggleContext} data-testid="inbox-toggle-context"><PanelRight className="mr-2 h-3.5 w-3.5" /> {contextOpen ? "Hide context" : "Show context"}</DropdownMenuItem> : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* commercial actions */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {opp ? (
            <Button asChild size="sm" variant="outline" className="h-8 gap-1.5 rounded-full bg-white">
              <Link to={`/opportunities/${opp.id}`} data-testid="inbox-open-opportunity"><Target className="h-3.5 w-3.5 text-primary" /> {opp.name} <ExternalLink className="h-3 w-3 text-muted-foreground" /></Link>
            </Button>
          ) : thread.ai?.intent !== "noise" && thread.ai?.intent !== "internal" ? (
            <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-full bg-white" onClick={onCreateOpportunity} disabled={busy} data-testid="inbox-create-opportunity"><Target className="h-3.5 w-3.5 text-primary" /> Create opportunity</Button>
          ) : null}
          {thread.ai?.intent !== "noise" ? (
            <Button size="sm" className="h-8 gap-1.5 rounded-full" onClick={onStartWorkspace} disabled={busy} data-testid="inbox-start-workspace"><Sparkles className="h-3.5 w-3.5" /> Start AI Workspace</Button>
          ) : null}
          {thread.snoozed_until && thread.status === "snoozed" ? <span className="ml-auto inline-flex items-center gap-1 text-[12px] text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Snoozed until {fmtDateTime(thread.snoozed_until)}</span> : null}
        </div>
      </header>

      {/* timeline */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-5 scrollbar-thin sm:px-6" data-testid="inbox-thread-timeline">
        <AiBrief ai={thread.ai} onUseDraft={useDraft} onAct={() => draft("concise")} />
        <div className="mt-6 space-y-5">
          {grouped.map((m) => <Message key={m.id} m={m} thread={thread} />)}
          {grouped.length === 0 ? <EmptyState title="No messages" description="This thread has no messages yet." /> : null}
        </div>
      </div>

      <Composer thread={thread} value={value} onChange={setValue} onSend={send} onDraft={draft} drafting={drafting} draftNotes={draftNotes} kind={kind} onKind={setKind} sending={sending} textareaRef={textareaRef} />
    </section>
  );
}
